import { GridSize } from '@mui/material/Grid';

export interface AssetPreview {
  size: number | null;
  width: number | null;
  height: number | null;
  square: boolean | null;
  href: string | null;
}

export interface AssetGetThumbnailUrl {
  previews: AssetPreview[];
  pixelWidth: string | null;
  pixelHeight: string | null;
}

export const prefixUrl = (
  preview: AssetPreview,
  baseUrl: string,
): AssetPreview => {
  if (preview.href && preview.href.startsWith('http')) {
    return preview;
  }
  return { ...preview, href: `${baseUrl}/${preview.href}` };
};

export enum PreviewSize {
  'xxs' = 200,
  'xs' = 400,
  'sm' = 600,
  'md' = 800,
  'lg' = 1200,
  'xl' = 1600,
  'xxl' = 2400,
}

export const selectMaxSize = (
  size: PreviewSize,
  maxWidth?: number,
  maxHeight?: number,
): PreviewSize => {
  if (maxWidth && maxHeight && (size > maxWidth || size > maxHeight)) {
    const possiblesSizes = Object.values(PreviewSize)
      .filter(Number)
      .filter(
        (key) => (key as number) <= maxWidth || (key as number) <= maxHeight,
      );
    // eslint-disable-next-line fp/no-mutating-methods
    return possiblesSizes.pop() as PreviewSize;
  }

  return size;
};

export const findAssetPreview = (
  previews: AssetPreview[],
  size: PreviewSize,
  baseUrl: string,
  square = false,
  maxWidth?: number,
  maxHeight?: number,
): AssetPreview | null => {
  const maxSize = selectMaxSize(size, maxWidth, maxHeight);

  const preview =
    previews.find((item) => item.size === maxSize && item.square === square) ||
    null;
  return preview && prefixUrl(preview, baseUrl);
};

export const getThumbnailSize = (gridSize: GridSize): PreviewSize => {
  if (typeof gridSize !== 'number') return PreviewSize.xxs;
  if (gridSize > 6) {
    return PreviewSize.xxl;
  } else if (gridSize >= 6) {
    return PreviewSize.xl;
  } else if (gridSize >= 5) {
    return PreviewSize.md;
  } else if (gridSize >= 3) {
    return PreviewSize.sm;
  } else if (gridSize >= 2) {
    return PreviewSize.xs;
  }
  return PreviewSize.xxs;
};

export const getThumbnailUrl = (
  asset: AssetGetThumbnailUrl,
  baseUrl: string,
  size: PreviewSize = PreviewSize.md,
): string | null => {
  let thumbnailUrl = null;

  if (asset.previews) {
    const maxWidth = asset.pixelWidth
      ? parseInt(asset.pixelWidth, 10)
      : undefined;
    const maxHeight = asset.pixelHeight
      ? parseInt(asset.pixelHeight, 10)
      : undefined;
    const thumbnail = findAssetPreview(
      asset.previews,
      size,
      baseUrl,
      false,
      maxWidth,
      maxHeight,
    );
    thumbnailUrl = (thumbnail && thumbnail.href) || null;
  }
  return thumbnailUrl;
};

export const encodeOrigin = (origin: string) =>
  encodeURIComponent(origin)
    .replace(/!/g, '%21')
    .replace(/'/g, '%27')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')
    .replace(/\*/g, '%2A')
    .replace(/%20/g, '+')
    .replace(/\./g, '%2E')
    .replace(/%/g, '.');

export const getOriginalImage = (canonical: string) => {
  const oneOriginalUrl = 'https://one.img.pmdstatic.net';
  const newUrl = `${oneOriginalUrl}/original/${encodeOrigin(
    canonical,
  )}/transformation.jpg`;
  return newUrl;
};
